import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import Modal from "react-modal";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import {
  convertStraight,
  formatDateTimezon,
  formattedLongDate2,
} from "../common-data/Converts";
import { CheckCircleIcon, XCircleIcon } from "../../images/icons/icons";
import CmnFilters from "../lead-management/common/CmnFilters";
import FitnessProfileCharts from "./FitnessProfileCharts";
import BCAStatCards from "./BCAStatCards";
import FitnessProfileStatCards from "./FitnessProfileStatCards";
import TableSkeleton from "../common-components/TableSkeleton";

const FitnessProfileTabs = ({
  isPage,
  setIsPage,
  pageLimit,
  setPageLimit,

  userInfoData,
  searchFilterApplied,
  allFilters,
  setAllFilters,
  onClickSearch,
  onResetFilters,
}) => {
  const {
    fitnessProfileLogs,
    fitnessProfileLogsLoading,
    fitnessProfileLogsError,

    bcaLogs,
    bcaLogsLoading,
    bcaLogsError,
  } = useSelector((state) => state.prospects);

  const [selectedTab, setSelectedTab] = useState(0);
  const iniMemData = {
    tbData: [],
    pagination: [],

    bcData: [],
    bcPagination: [],
  };
  const [membersData, setMembersData] = useState(iniMemData);

  const iniModValues = {
    isModOpen: false,
    modName: "",
    rowData: "",
  };
  const [modValues, setModValues] = useState(iniModValues);

  const closeModal = () => {
    setModValues(iniModValues);
  };

  const handleTabSelect = (index) => {
    setSelectedTab(index);
    setIsPage(0);
    setPageLimit(10);
  };

  useEffect(() => {
    if (
      !fitnessProfileLogsLoading &&
      !fitnessProfileLogsError &&
      fitnessProfileLogs?.status &&
      fitnessProfileLogs?.data?.length > 0
    ) {
      setMembersData((prevs) => ({
        ...prevs,
        tbData: fitnessProfileLogs.data || [],
        pagination: fitnessProfileLogs?.pagination || [],
      }));
    } else {
      setMembersData((prevs) => ({
        ...prevs,
        tbData: [],
        pagination: [],
      }));
    }
  }, [fitnessProfileLogsLoading, fitnessProfileLogsError, fitnessProfileLogs]);

  useEffect(() => {
    if (
      !bcaLogsLoading &&
      !bcaLogsError &&
      bcaLogs?.status &&
      bcaLogs?.data?.length > 0
    ) {
      setMembersData((prevs) => ({
        ...prevs,
        bcData: bcaLogs.data || [],
        bcPagination: bcaLogs?.pagination || [],
      }));
    } else {
      setMembersData((prevs) => ({
        ...prevs,
        bcData: [],
        bcPagination: [],
      }));
    }
  }, [bcaLogsLoading, bcaLogsError, bcaLogs]);

  const tbColumns = [
    {
      dataField: "date_added",
      text: "Last Done On",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.date_added ? formattedLongDate2(row.date_added) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "user_id",
      text: "Member Info",
      formatter: (_, row) => {
        return (
          <span
            className="tb-data minWidth100 link"
            onClick={() =>
              setModValues((prevs) => ({
                ...prevs,
                isModOpen: true,
                modName: "fitness-profile-logs",
                rowData: row,
              }))
            }
          >
            {row?.name || "NA"}

            <br />
            <small style={{ textTransform: "lowercase" }}>
              {row?.mobile || ""}
              <br />
              {row?.email || ""}
            </small>
          </span>
        );
      },
    },
    {
      dataField: "fitness_status",
      text: "status",
      formatter: (_, row) => {
        return (
          <span
            className={`tb-data minWidth100 ${
              !row?.fitness_status ? "" : "withPadding borderRdius27"
            }`}
            style={{
              background: !row?.fitness_status
                ? "transparent"
                : row?.fitness_status?.toLowerCase() === "completed"
                ? "#379C00"
                : row?.fitness_status?.toLowerCase() === "upcoming"
                ? "#157EF9"
                : row?.fitness_status?.toLowerCase() === "missed"
                ? "#D90000"
                : "#FB9E3A",
              color: !row?.fitness_status ? "#000" : "#fff",
            }}
          >
            {row?.fitness_status || "NA"}
          </span>
        );
      },
    },
    {
      dataField: "gym_name",
      text: "gym",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth150">{row?.gym_name || "NA"}</span>
        );
      },
    },
    {
      dataField: "trainer_id",
      text: "trainer",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.trainer_name || "NA"}</span>;
      },
    },
    {
      dataField: "gender",
      text: "gender",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.gender || "NA"}</span>;
      },
    },
    {
      dataField: "age",
      text: "age",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.age || "-"}</span>;
      },
    },
    {
      dataField: "height",
      text: "height",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {parseFloat(row?.height || 0).toFixed(2)} CM
          </span>
        );
      },
    },
    {
      dataField: "current_weight",
      text: "current weight",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {parseFloat(row?.current_weight || 0).toFixed(2)} KG
          </span>
        );
      },
    },
    {
      dataField: "target_weight",
      text: "target weight",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {parseFloat(row?.target_weight || 0).toFixed(2)} KG
          </span>
        );
      },
    },
    {
      dataField: "current_calorie_result",
      text: "current calorie",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {parseFloat(row?.current_calorie_result || 0).toFixed(2)}
          </span>
        );
      },
    },
    {
      dataField: "calorie_target_result",
      text: "target calorie",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {parseFloat(row?.calorie_target_result || 0).toFixed(2)}
          </span>
        );
      },
    },
    {
      dataField: "bmi_result",
      text: "BMI",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {parseFloat(row?.bmi_result || 0).toFixed(2)}
          </span>
        );
      },
    },
    {
      dataField: "tdee",
      text: "TDEE",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {parseFloat(row?.tdee || 0).toFixed(2)}
          </span>
        );
      },
    },
    {
      dataField: "fat_result",
      text: "FAT",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {parseFloat(row?.fat_result || 0).toFixed(2)}
          </span>
        );
      },
    },
    {
      dataField: "how_active",
      text: "how active",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.how_active?.replace(/_/g, " ") || "-"}
          </span>
        );
      },
    },
    {
      dataField: "goal",
      text: "goal",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.goal?.replace(/_/g, " ") || "-"}
          </span>
        );
      },
    },
    {
      dataField: "body_type",
      text: "body type",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.body_type?.replace(/_/g, " ") || "-"}
          </span>
        );
      },
    },
    {
      dataField: "result_value",
      text: "fitness result",
      formatter: (_, row) => {
        return (
          <span className="tb-data">
            {row?.result_value?.replace(/_/g, " ") || "-"}
          </span>
        );
      },
    },
    {
      dataField: "is_new",
      text: "New member",
      formatter: (_, row) => {
        return (
          <span className="tb-data icon">
            {row?.is_new ? <CheckCircleIcon /> : <XCircleIcon />}
          </span>
        );
      },
    },
  ];

  const bcaColumns = [
    {
      dataField: "date_added",
      text: "Last Done On",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.date_added ? formatDateTimezon(row.date_added) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "user_id",
      text: "Member Info",
      formatter: (_, row) => {
        return (
          <span
            className="tb-data minWidth100 link"
            onClick={() =>
              setModValues((prevs) => ({
                ...prevs,
                isModOpen: true,
                modName: "bca-logs",
                rowData: row,
              }))
            }
          >
            {row?.name || "NA"}

            <br />
            <small style={{ textTransform: "lowercase" }}>
              {row?.mobile || ""}
              <br />
              {row?.email || ""}
            </small>
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "BCA status",
      formatter: (_, row) => {
        return (
          <>
            <span
              className={`tb-data minWidth100 ${
                !row?.status ? "" : "withPadding borderRdius27"
              }`}
              style={{
                background: !row?.status
                  ? "transparent"
                  : row?.status?.toLowerCase() === "completed"
                  ? "#379C00"
                  : row?.status?.toLowerCase() === "upcoming"
                  ? "#157EF9"
                  : row?.status?.toLowerCase() === "missed"
                  ? "#D90000"
                  : "#FB9E3A",
                color: !row?.status ? "#000" : "#fff",
              }}
            >
              {row?.status || "NA"}
            </span>

            {row?.status === "completed" && (
              <>
                <br />

                <small className="tb-data underline">
                  {row?.completed_on ? convertStraight(row.completed_on) : "-"}
                </small>
              </>
            )}
          </>
        );
      },
    },
    {
      dataField: "gym_id",
      text: "gym",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth150">{row?.gym_name || "NA"}</span>
        );
      },
    },
    {
      dataField: "trainer_id",
      text: "trainer",
      formatter: (_, row) => {
        return <span className="tb-data">{row?.trainer_name || "NA"}</span>;
      },
    },
    {
      dataField: "scheduled_date",
      text: "scheduled date",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.scheduled_date ? convertStraight(row.scheduled_date) : "-"}
          </span>
        );
      },
    },
    {
      dataField: "next_scheduled_date",
      text: "next scheduled date",
      formatter: (_, row) => {
        return (
          <span className="tb-data minWidth100">
            {row?.next_scheduled_date
              ? convertStraight(row.next_scheduled_date)
              : "-"}
          </span>
        );
      },
    },
    {
      dataField: "scheduled_trainer",
      text: "Next scheduled trainer",
      formatter: (_, row) => {
        return (
          <span className="tb-data">{row?.scheduled_trainer_name || "NA"}</span>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        isOpen={modValues.isModOpen}
        onRequestClose={closeModal}
        contentLabel="Add Modal"
        ariaHideApp={false}
      >
        <div
          className={`createNewCTOModalContainer mod-height-95vh ${
            modValues.modName === "fitness-profile-logs" ||
            modValues.modName === "bca-logs"
              ? "mod-width-70p full-container-width bg-white"
              : "mod-width-70p"
          }`}
        >
          {modValues.modName === "fitness-profile-logs" ||
          modValues.modName === "bca-logs" ? (
            <FitnessProfileCharts
              rowData={modValues.rowData}
              userInfoData={userInfoData}
              onClose={closeModal}
              isBCALogs={modValues.modName === "bca-logs"}
            />
          ) : null}
        </div>
      </Modal>

      <div className="blog-form-tabs-container mt30">
        <Tabs selectedIndex={selectedTab} onSelect={handleTabSelect}>
          <TabList>
            <Tab>Fitness Profile Logs</Tab>
            <Tab>BCA Logs</Tab>
          </TabList>

          <TabPanel>
            <CmnFilters
              from="fitness-profile-logs"
              userInfoData={userInfoData}
              searchFilterApplied={searchFilterApplied}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
              onClickReset={onResetFilters}
              isLoading={fitnessProfileLogsLoading}
              searchPlaceHolder="Search by member name/mobile/email..."
            />

            <FitnessProfileStatCards
              setFilters={setAllFilters}
              selectedRange={allFilters.fitness_milestone}
            />

            <div className="fullWidthFlexCenter overflowAuto separatedTable separatedTable no-border-table mt15">
              {fitnessProfileLogsLoading ? (
                <TableSkeleton />
              ) : (
                <PaymentLinkTable
                  tableData={membersData.tbData}
                  tableColumns={tbColumns}
                  tablePagination={membersData.pagination}
                  isPage={isPage}
                  setIsPage={(value) => setIsPage(value)}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </TabPanel>

          <TabPanel>
            <CmnFilters
              // from="bca-logs"
              from="fitness-profile-logs"
              userInfoData={userInfoData}
              searchFilterApplied={searchFilterApplied}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
              onClickReset={onResetFilters}
              isLoading={bcaLogsLoading}
              searchPlaceHolder="Search by member name/mobile/email..."
              clickedCard="bca-logs"
            />

            <BCAStatCards
              setFilters={setAllFilters}
              selectedRange={allFilters.pending_days_range}
            />

            <div className="fullWidthFlexCenter overflowAuto separatedTable separatedTable no-border-table mt15">
              {bcaLogsLoading ? (
                <TableSkeleton />
              ) : (
                <PaymentLinkTable
                  tableData={membersData.bcData}
                  tableColumns={bcaColumns}
                  tablePagination={membersData.bcPagination}
                  isPage={isPage}
                  setIsPage={(value) => setIsPage(value)}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </TabPanel>
        </Tabs>
      </div>
    </>
  );
};

export default FitnessProfileTabs;
